import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { get, put } from 'aws-amplify/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/management.css';

function InvitationGuestConfirmScreen() {
  const { invitationId } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [guestData, setGuestData] = useState({
    firstName: '',
    shortName: '',    
    lastName: '',
    email: '',
    phone: ''
  });
  const [validationErrors, setValidationErrors] = useState({
    firstName: '',
    shortName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchInvitation = useCallback(async () => {
    try {
      console.log('Fetching invitation data...');
      const restOperation = get({
        apiName: 'events',
        path: `/invitations/${invitationId}/guest`,
      });
      const response = await restOperation.response;
      const { body } = response;
      const data = await body.json();
      console.log('Received invitation data:', data);
      
      setInvitation(data);
      if (data.guest) {
        setGuestData({
            firstName: data.guest.firstName || '',
            shortName: data.guest.shortName || '',            
            lastName: data.guest.lastName || '',
            email: data.guest.email || '',
            phone: data.guest.phone || ''
          });
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setError('Failed to load invitation. Please check the URL and try again.');
      setLoading(false);
    }
  }, [invitationId]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  const validateField = (name, value) => {
    let error = '';
    
    switch(name) {
      case 'firstName':
      case 'shortName':
      case 'lastName':
        if (value.length > 50) {
          error = `Name fields must be less than 50 characters`;
        }
        break;
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (value && !/^\+?[\d\s-()]{10,}$/.test(value)) {
          error = 'Please enter a valid phone number';
        }
        break;
      default:
        break;
    }
    return error;
  };

  const invalidGuestContact = guests.filter(guest => 
    guest.sendInvitation && (
      (guest.contactMethod === 'email' && !guest.email.trim()) ||
      (guest.contactMethod === 'phone' && !guest.phone.trim())
    )
  );
  
  if (invalidGuestContact.length > 0) {
    setError('All guests receiving invitations must have their chosen contact method filled out');
    return;
  }


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('Input change:', name, value);
    
    const error = validateField(name, value);
    
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));
    
    setGuestData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission initiated');
    
    // Check for validation errors
    const errors = {};
    Object.keys(guestData).forEach(field => {
      const error = validateField(field, guestData[field]);
      if (error) errors[field] = error;
    });
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError('Please correct the errors before submitting.');
      return;
    }
    
    try {
      setError(null);
      setSuccessMessage('');
      console.log('Submitting guest data:', guestData);
      
      const restOperation = put({
        apiName: 'events',
        path: `/invitations/${invitationId}/guest/confirm`,
        options: {
          body: guestData,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });
      const response = await restOperation.response;
      const { body } = response;
      const data = await body.json();
      console.log('Guest confirmation response:', data);
      
      setSuccessMessage('Thank you for confirming your details!');
      setGuestData(prev => ({
        ...prev,
        confirmed: true
      }));
    } catch (err) {
      console.error('Failed to update guest information:', err);
      console.error('Error details:', err);
      setError('Failed to update guest information. Please try again.');
    }
  };

  if (loading) return <LoadingSpinner message="Loading guest confirmation..." />;
  if (error) return <div className="error">{error}</div>;
  if (!invitation) return <div>Invitation not found</div>;

  return (
    <div className="guest-confirm-screen">
      <h1>Confirm Your Details</h1>
      
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      <div className="event-details">
        <h2>{invitation.event?.name}</h2>
        <p>{invitation.event?.description}</p>
        <p>Date: {new Date(invitation.event?.date).toLocaleDateString()}</p>
        <p>Location: {invitation.event?.location}</p>
      </div>

      <form 
        onSubmit={handleSubmit}
        className="guest-form">
        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={guestData.firstName}
            onChange={handleInputChange}
            required
            autoComplete="given-name"
            tabIndex={1}
            aria-label="First Name"
          />
          {validationErrors.firstName && (
            <div className="error-message">{validationErrors.firstName}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="shortName">Short Name:</label>
          <input
            type="text"
            id="shortName"
            name="shortName"
            value={guestData.shortName}
            onChange={handleInputChange}
            required
            autoComplete="short-name"
            tabIndex={2}
            aria-label="Short Name"
          />
          {validationErrors.firstName && (
            <div className="error-message">{validationErrors.firstName}</div>
          )}


          <label htmlFor="lastName">Last Name:</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={guestData.lastName}
            onChange={handleInputChange}
            required
            autoComplete="family-name"
            tabIndex={3}
            aria-label="Last Name"
          />
          {validationErrors.lastName && (
            <div className="error-message">{validationErrors.lastName}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={guestData.email}
            onChange={handleInputChange}
            autoComplete="email"
            tabIndex={4}
            aria-label="Email"
          />
          {validationErrors.email && (
            <div className="error-message">{validationErrors.email}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone:</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={guestData.phone}
            onChange={handleInputChange}
            autoComplete="tel"
            tabIndex={5}
            aria-label="Phone number"
          />
          {validationErrors.phone && (
            <div className="error-message">{validationErrors.phone}</div>
          )}
        </div>

        <button 
          type="submit" 
          className="confirm-button"
          tabIndex={6}
          aria-label="Confirm Details">
          Confirm Details
        </button>
      </form>
    </div>
  );
}

export default InvitationGuestConfirmScreen;